import { useTranslation } from 'react-i18next'
import { Check, ChevronsUpDown } from 'lucide-react'
import { toast } from 'sonner'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar'
import { LogoTile } from '@/components/brand/logo'
import { useCurrentTeam } from '@/features/team/current-team'
import { useSwitchTeam, useTeams } from '@/features/team/queries'

export function TeamSwitcher() {
  const { t } = useTranslation()
  const { isMobile } = useSidebar()
  const { current } = useCurrentTeam()
  const { data: teams = [] } = useTeams()
  const switchTeam = useSwitchTeam()

  const name = current?.team.name ?? ''
  const role = current?.role === 'admin' ? t('common.admin') : t('common.user')

  const onSelect = (id: number, selected?: boolean) => {
    if (selected) return
    switchTeam.mutate(id, { onError: () => toast.error(t('v2.team.switchFailed')) })
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent" disabled={switchTeam.isPending}>
              <LogoTile className="shrink-0 rounded-[7px] shadow-sm" />
              <span className="grid flex-1 text-left leading-tight">
                <span className="truncate font-semibold" title={name}>{name}</span>
                <span className="truncate text-xs text-muted-foreground">{role}</span>
              </span>
              <ChevronsUpDown className="ml-auto text-muted-foreground" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="min-w-60" align="start" side={isMobile ? 'bottom' : 'right'} sideOffset={4}>
            <DropdownMenuLabel className="text-xs text-muted-foreground">{t('v2.team.yourTeams')}</DropdownMenuLabel>
            {teams.map((team) => (
              <DropdownMenuItem key={team.id} onSelect={() => onSelect(team.id, team.selected)} className="gap-2">
                <span className="flex size-6 items-center justify-center rounded-sm bg-muted text-[11px] font-semibold">
                  {initials(team.name)}
                </span>
                <span className="truncate">{team.name}</span>
                {team.selected && <Check className="ml-auto text-primary" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}
